// src/pages/Galerie.js
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import "../index.css";
import useSEO from "../seo/useSEO";

// 📸 Liste d'images — adapte si tu veux des captions plus tard
const IMAGES = Array.from({ length: 63 }, (_, i) => ({
  id: `photo-${i + 1}`,
  src: `/images/gallery/photo${i + 1}.jpg`,
  alt: `Photo ${i + 1} — HLBK`,
  caption: `© HLBK — Photo ${i + 1}/45`,
}));

export default function Galerie() {
useSEO({
    title: "Galerie Photo | Mariage, Portrait & Entreprise — HLBK",
    description:
      "Découvrez une sélection de photographies HLBK : mariage, portrait, famille, événement et photographie professionnelle à Nancy et dans le Grand Est.",
    canonical: "https://www.hlbk.fr/galerie",
    image: "https://www.hlbk.fr/images/og-default.jpg",
  });

  const [currentIndex, setCurrentIndex] = useState(null); // index dans shuffledImages
  const startX = useRef(null);

  // 🔀 Mélange des images (une seule fois)
  const shuffledImages = useMemo(() => {
    const arr = [...IMAGES];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, []);

  const open = useCallback((idx) => setCurrentIndex(idx), []);
  const close = useCallback(() => setCurrentIndex(null), []);

  const next = useCallback(() => {
    setCurrentIndex((i) => {
      if (i === null) return 0;
      return (i + 1) % shuffledImages.length;
    });
  }, [shuffledImages.length]);

  const prev = useCallback(() => {
    setCurrentIndex((i) => {
      if (i === null) return 0;
      return (i - 1 + shuffledImages.length) % shuffledImages.length;
    });
  }, [shuffledImages.length]);

  // ESC / flèches clavier + lock scroll
  useEffect(() => {
    const onKey = (e) => {
      if (currentIndex === null) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };

    document.addEventListener("keydown", onKey);

    if (currentIndex !== null) {
      const old = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = old;
        document.removeEventListener("keydown", onKey);
      };
    }

    return () => document.removeEventListener("keydown", onKey);
  }, [currentIndex, close, next, prev]);

  // Swipe mobile
  const onTouchStart = (e) => (startX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    startX.current = null;
  };

  const active = currentIndex !== null ? shuffledImages[currentIndex] : null;

  return (
    <main className="main main--wide">
      <section className="gallery-page">
        {/* Grille style MaximeH : boutons cliquables */}
        <div className="gallery">
          {shuffledImages.map((img, idx) => (
            <button
              key={img.id}
              type="button"
              className="gallery-item"
              onClick={() => open(idx)}
              aria-label={`Ouvrir ${img.alt}`}
            >
              <img src={img.src} alt={img.alt} loading="lazy" />
            </button>
          ))}
        </div>
      </section>

      {/* Modale */}
      {active && (
        <div className="modal" onClick={close}>
          <button
            className="lb-close"
            aria-label="Fermer"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
          >
            ×
          </button>

          <button
            className="lb-arrow lb-prev"
            aria-label="Précédent"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
          >
            ‹
          </button>

          <div
            className="modal-inner"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <img src={active.src} alt={active.alt} />
            <div className="lb-caption">{active.caption}</div>
          </div>

          <button
            className="lb-arrow lb-next"
            aria-label="Suivant"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
          >
            ›
          </button>
        </div>
      )}
    </main>
  );
}